import React from 'react';
import { Entity } from '@backstage/catalog-model';
import { Link } from '@backstage/core-components';
import { useRouteRef } from '@backstage/core-plugin-api';
import {
  entityRouteParams,
  entityRouteRef,
} from '@backstage/plugin-catalog-react';
import { Chip, Tooltip, Typography, makeStyles } from '@material-ui/core';

/**
 * What the categories of `spec.type` are called. A category that is not in
 * here is shown as it is written.
 */
const CATEGORIES: Record<string, string> = {
  ai: 'AI',
  'ci-cd': 'CI/CD',
  cloud: 'Cloud',
  compute: 'Compute',
  consulting: 'Consulting',
  data: 'Data',
  database: 'Databases',
  'developer-portal': 'Developer portal',
  itsm: 'IT service management',
  kubernetes: 'Kubernetes',
  messaging: 'Messaging',
  observability: 'Observability',
  storage: 'Storage',
};

/** How many services the chips show before the rest is left to the page. */
const CHIPS_SHOWN = 6;

const useStyles = makeStyles(theme => ({
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
  },
  deprecated: {
    opacity: 0.55,
    textDecoration: 'line-through',
  },
}));

const titleOf = (service: Entity) =>
  service.metadata.title ?? service.metadata.name;

/**
 * What the tooltip of a service says: its category, what it is, and whether
 * it is on its way out.
 */
function tooltipOf(service: Entity): string {
  const type = service.spec?.type as string | undefined;
  const category = type ? (CATEGORIES[type] ?? type) : undefined;
  const description = service.metadata.description;
  const deprecated = service.spec?.lifecycle === 'deprecated';
  return [
    [category, description].filter(Boolean).join(' - '),
    deprecated ? '(deprecated)' : undefined,
  ]
    .filter(Boolean)
    .join(' ');
}

/**
 * The services a team offers, a chip each leading to the page of the service,
 * the first few of them. A service on its way out is faded and struck through.
 * Nothing for a team that offers none.
 */
export const ServiceList = (props: { services: Entity[] }) => {
  const { services } = props;
  const classes = useStyles();
  const entityRoute = useRouteRef(entityRouteRef);

  if (services.length === 0) {
    return null;
  }
  const rest = services.length - CHIPS_SHOWN;
  return (
    <div className={classes.chips}>
      {services.slice(0, CHIPS_SHOWN).map(service => {
        const deprecated = service.spec?.lifecycle === 'deprecated';
        const chip = (
          <Chip
            size="small"
            clickable
            label={titleOf(service)}
            component={Link as any}
            to={entityRoute(entityRouteParams(service))}
            className={deprecated ? classes.deprecated : undefined}
          />
        );
        const tooltip = tooltipOf(service);
        return tooltip ? (
          <Tooltip key={service.metadata.name} title={tooltip}>
            {chip}
          </Tooltip>
        ) : (
          <React.Fragment key={service.metadata.name}>{chip}</React.Fragment>
        );
      })}
      {rest > 0 && (
        <Typography variant="body2" color="textSecondary">
          +{rest} more
        </Typography>
      )}
    </div>
  );
};
